import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CreditCard, CheckCircle, Clock, AlertTriangle, Loader2,
  ChevronRight, FileText, Smartphone, Copy, Check, X, Sparkles, ExternalLink, RefreshCw, Upload, Image
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { DashboardLayout } from "./Dashboard";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";

const PharmacySubscription = () => {
  const { t, i18n } = useTranslation("pharmacy");
  const dateLocale = i18n.language === "en" ? "en-US" : "fr-FR";
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [mobileMoneyPayments, setMobileMoneyPayments] = useState([]);
  const [stripePayments, setStripePayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stripePackages, setStripePackages] = useState([]);
  const [mobileMoneyAccounts, setMobileMoneyAccounts] = useState({});
  const [notice, setNotice] = useState("");

  // Payment flow state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentStep, setPaymentStep] = useState("duration"); // duration, method, mobilemoney, payment-form, stripe
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedStripePackage, setSelectedStripePackage] = useState(null);
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // Duration selection
  const [selectedDuration, setSelectedDuration] = useState(1);
  const durationOptions = [
    { months: 1, label: t("subscription.duration1"), discount: 0 },
    { months: 3, label: t("subscription.duration3"), discount: 5, savings: t("subscription.save5") },
    { months: 6, label: t("subscription.duration6"), discount: 10, savings: t("subscription.save10") },
    { months: 12, label: t("subscription.duration12"), discount: 15, savings: t("subscription.save15"), popular: true }
  ];
  
  // New fields for {t("subscription.mobileMoney")} payment form
  const [senderPhone, setSenderPhone] = useState("");
  const [senderName, setSenderName] = useState("");
  const [transactionReference, setTransactionReference] = useState("");
  const [paymentComment, setPaymentComment] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [senderProvider, setSenderProvider] = useState("");

  useEffect(() => {
    fetchData();
    
    // Check for Stripe redirect
    const sessionId = searchParams.get("session_id");
    const status = searchParams.get("status");
    
    if (sessionId && status === "success") {
      handleStripeReturn(sessionId);
    } else if (status === "cancelled") {
      toast.info(t("subscription.toastCancelled"));
      setSearchParams({});
    }
  }, []);

  const handleStripeReturn = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const pollStatus = async () => {
      try {
        const res = await apiClient.get(`/stripe/status/${sessionId}`);
        
        if (res.data.payment_status === "paid") {
          toast.success(t("subscription.toastPaymentSuccess"));
          setSearchParams({});
          fetchData();
          setCheckingPayment(false);
          return;
        } else if (res.data.status === "expired") {
          toast.error(t("subscription.toastSessionExpired"));
          setSearchParams({});
          setCheckingPayment(false);
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          toast.info(t("subscription.toastCheckingRefresh"));
          setCheckingPayment(false);
        }
      } catch (error) {
        console.error("Error checking payment:", error);
        setCheckingPayment(false);
      }
    };

    pollStatus();
  };

  const fetchData = async () => {
    try {
      const [subRes, plansRes, accountsRes, mmPaymentsRes, stripePlansRes, stripeHistoryRes] = await Promise.all([
        apiClient.get("/pharmacy/subscription"),
        apiClient.get("/subscription/plans"),
        apiClient.get("/subscription/mobilemoney-accounts"),
        apiClient.get("/subscription/mobilemoney/status").catch(() => ({ data: [] })),
        apiClient.get("/stripe/plans").catch(() => ({ data: { plans: [] } })),
        apiClient.get("/stripe/history").catch(() => ({ data: { transactions: [] } }))
      ]);

      setSubscription(subRes.data.subscription);
      setPayments(subRes.data.payments || []);
      setPlans(plansRes.data);
      setMobileMoneyAccounts(accountsRes.data.accounts || {});
      setNotice(accountsRes.data.notice || "");
      setMobileMoneyPayments(mmPaymentsRes.data || []);
      setStripePackages(stripePlansRes.data.plans || []);
      setStripePayments(stripeHistoryRes.data.transactions || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, providerId) => {
    navigator.clipboard.writeText(text);
    setCopiedId(providerId);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success(t("subscription.toastCopied"));
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowPaymentDialog(true);
    setPaymentStep("duration");
    setPaymentMethod("mobilemoney");
    setSelectedProvider("");
    setReference("");
  };

  const handleRenew = () => {
    // Pre-select the current subscription plan when clicking renew
    const currentPlan = plans.find((p) => p.id === subscription?.plan_id);
    if (currentPlan) {
      handleSelectPlan(currentPlan);
    } else if (plans.length > 0) {
      // Fallback: open the dialog with the first available plan
      handleSelectPlan(plans[0]);
    } else {
      toast.error(t("subscription.toastNoPlan"));
    }
  };

  const handleMethodContinue = () => {
    if (paymentMethod === "mobilemoney") {
      setPaymentStep("mobilemoney");
    } else if (paymentMethod === "stripe") {
      setPaymentStep("stripe");
    }
  };

  const handleStripeCheckout = async () => {
    if (!selectedStripePackage) {
      toast.error(t("subscription.toastSelectPackage"));
      return;
    }

    setSubmitting(true);
    try {
      const originUrl = window.location.origin;
      
      const res = await apiClient.post("/stripe/checkout", {
        plan_id: selectedStripePackage.id,
        origin_url: originUrl
      });

      // Redirect to Stripe Checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Stripe error:", error);
      toastApiError(error);
      setSubmitting(false);
    }
  };

  const handleProviderContinue = () => {
    if (!selectedProvider) {
      toast.error(t("subscription.toastSelectProvider"));
      return;
    }
    setPaymentStep("payment-form");
  };

  const handleSubmitReference = async () => {
    // Safety: ensure a plan is selected (prevents "null is not an object" crash)
    if (!selectedPlan || !selectedPlan.id) {
      toast.error(t("subscription.toastNoPlanSelected"));
      return;
    }
    // Validation des champs obligatoires
    if (!senderPhone.trim()) {
      toast.error(t("subscription.toastEnterPhone"));
      return;
    }
    if (!senderName.trim()) {
      toast.error(t("subscription.toastEnterSender"));
      return;
    }
    if (!transactionReference.trim()) {
      toast.error(t("subscription.toastEnterRef"));
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("plan_id", selectedPlan.id);
      formData.append("provider", selectedProvider || senderProvider.trim() || "other");
      formData.append("sender_phone", senderPhone.trim());
      formData.append("sender_name", senderName.trim());
      formData.append("transaction_reference", transactionReference.trim());
      formData.append("duration_months", selectedDuration.toString());
      if (paymentComment.trim()) {
        formData.append("comment", paymentComment.trim());
      }
      if (proofFile) {
        formData.append("proof_file", proofFile);
      }

      await apiClient.post(
        "/subscription/mobilemoney/request",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      toast.success(t("subscription.toastSubmitted"));
      setShowPaymentDialog(false);
      resetPaymentForm();
      fetchData();
    } catch (error) {
      console.error("Mobile Money submission error:", error);
      toastApiError(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  const resetPaymentForm = () => {
    setPaymentStep("duration");
    setSelectedProvider("");
    setSelectedStripePackage(null);
    setReference("");
    setSenderPhone("");
    setSenderName("");
    setTransactionReference("");
    setPaymentComment("");
    setSelectedDuration(1);
    setProofFile(null);
    setSenderProvider("");
    if (proofPreview) {
      URL.revokeObjectURL(proofPreview);
      setProofPreview(null);
    }
  };

  const closeDialog = () => {
    setShowPaymentDialog(false);
    resetPaymentForm();
  };

  const formatUSD = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: t("status.active"), className: "bg-green-100 text-green-700" },
      pending: { label: t("status.pending"), className: "bg-amber-100 text-amber-700" },
      expired: { label: t("status.expired"), className: "bg-red-100 text-red-700" },
      approved: { label: t("status.approved"), className: "bg-green-100 text-green-700" },
      rejected: { label: t("status.rejected"), className: "bg-red-100 text-red-700" }
    };
    const c = config[status] || config.pending;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(dateLocale, {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(dateLocale).format(price);
  };

  // Calculate price with duration discount
  const calculatePriceWithDuration = (basePrice, months) => {
    const option = durationOptions.find(o => o.months === months) || durationOptions[0];
    const totalWithoutDiscount = basePrice * months;
    const discountAmount = totalWithoutDiscount * (option.discount / 100);
    const finalPrice = totalWithoutDiscount - discountAmount;
    return {
      basePrice,
      months,
      totalWithoutDiscount,
      discountPercent: option.discount,
      discountAmount,
      finalPrice,
      pricePerMonth: finalPrice / months
    };
  };

  const selectedPriceInfo = selectedPlan ? calculatePriceWithDuration(selectedPlan.price, selectedDuration) : null;

  if (loading) {
    return (
      <DashboardLayout title={t("subscription.title")}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-10 w-10 animate-spin text-omu-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  // Show checking payment overlay
  if (checkingPayment) {
    return (
      <DashboardLayout title={t("subscription.title")}>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-omu-blue-500" />
          <p className="text-lg font-medium text-slate-700">{t("subscription.checkingPayment")}</p>
          <p className="text-sm text-slate-500">{t("subscription.pleaseWait")}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t("subscription.title")}>
      <div className="space-y-6">
        {/* Current Subscription - Enhanced */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-omu-blue-500" />
              {t("subscription.mySubscription")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {subscription ? (
              <div className="space-y-4">
                {/* Subscription Status Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{subscription.plan_name}</p>
                    <p className="text-slate-500">
                      {subscription.effective_status === "active" ? (
                        <>{t("subscription.expiresOn", { date: subscription.end_date_formatted || formatDate(subscription.end_date) })}</>
                      ) : subscription.effective_status === "expired" ? (
                        <>{t("subscription.expiredOn", { date: subscription.end_date_formatted || formatDate(subscription.end_date) })}</>
                      ) : (
                        <>{t("subscription.awaitingValidation")}</>
                      )}
                    </p>
                  </div>
                  {getStatusBadge(subscription.effective_status || subscription.status)}
                </div>

                {/* Days Remaining Counter */}
                {subscription.effective_status === "active" && (
                  <div className={`p-4 rounded-xl ${
                    subscription.expires_soon 
                      ? "bg-amber-50 border border-amber-200" 
                      : "bg-green-50 border border-green-200"
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          subscription.expires_soon ? "bg-amber-100" : "bg-green-100"
                        }`}>
                          <Clock className={`h-6 w-6 ${
                            subscription.expires_soon ? "text-amber-600" : "text-green-600"
                          }`} />
                        </div>
                        <div>
                          <p className={`text-3xl font-bold ${
                            subscription.expires_soon ? "text-amber-700" : "text-green-700"
                          }`}>
                            {subscription.days_remaining}
                          </p>
                          <p className={`text-sm ${
                            subscription.expires_soon ? "text-amber-600" : "text-green-600"
                          }`}>
                            {subscription.days_remaining === 1 ? t("subscription.dayRemaining") : t("subscription.daysRemaining")}
                          </p>
                        </div>
                      </div>
                      {subscription.expires_soon && (
                        <Button
                          className="bg-amber-500 hover:bg-amber-600"
                          onClick={handleRenew}
                          data-testid="renew-btn-expires-soon"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          {t("actions.renew")}
                        </Button>
                      )}
                    </div>
                    {subscription.expires_soon && (
                      <p className="text-amber-700 text-sm mt-3">
                        {t("subscription.expiresSoonHint")}
                      </p>
                    )}
                  </div>
                )}

                {/* Expired State */}
                {subscription.effective_status === "expired" && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-6 w-6 text-red-500" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">{t("subscription.expiredTitle")}</p>
                        <p className="text-sm text-red-600">
                          {t("subscription.expiredDesc")}
                        </p>
                      </div>
                      <Button
                        className="bg-red-500 hover:bg-red-600"
                        onClick={handleRenew}
                        data-testid="renew-btn-expired"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {t("actions.renew")}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Pending State */}
                {subscription.effective_status === "pending" && (
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-3">
                      <Clock className="h-6 w-6 text-amber-500" />
                      <div>
                        <p className="font-medium text-amber-800">{t("subscription.pendingPaymentTitle")}</p>
                        <p className="text-sm text-amber-600">
                          {t("subscription.pendingPaymentDesc")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <AlertTriangle className="h-12 w-12 mx-auto text-amber-400 mb-3" />
                <p className="text-slate-600">{t("subscription.noActiveSubscription")}</p>
                <p className="text-sm text-slate-400 mt-1">
                  {t("subscription.subscribeHint")}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending {t("subscription.mobileMoney")} Payments */}
        {mobileMoneyPayments.filter(p => p.status === "pending").length > 0 && (
          <Alert className="border-amber-200 bg-amber-50">
            <Clock className="h-5 w-5 text-amber-600" />
            <AlertDescription className="text-amber-800">
              {t("subscription.pendingPaymentsAlert", { count: mobileMoneyPayments.filter(p => p.status === "pending").length })}
            </AlertDescription>
          </Alert>
        )}

        {/* Plans */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{t("subscription.availablePlans")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`border-2 rounded-2xl p-6 transition-all ${
                    selectedPlan?.id === plan.id
                      ? "border-omu-blue-500 bg-omu-blue-50"
                      : "border-slate-200 hover:border-omu-blue-300"
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-heading text-xl font-bold">{plan.name}</h3>
                      <p className="text-slate-500">{plan.description}</p>
                    </div>
                    {plan.name === "Pro" && (
                      <Badge className="bg-omu-blue-500">{t("subscription.popular")}</Badge>
                    )}
                  </div>

                  <div className="mb-4">
                    <span className="text-3xl font-bold text-slate-900">
                      {formatPrice(plan.price)}
                    </span>
                    <span className="text-slate-500 ml-1">
                      {plan.currency}/{plan.frequency === "monthly" ? t("subscription.perMonth") : t("subscription.perYear")}
                    </span>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full rounded-full ${
                      subscription?.plan_id === plan.id
                        ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                        : "bg-omu-blue-500 hover:bg-omu-blue-600"
                    }`}
                    disabled={subscription?.plan_id === plan.id && subscription?.status === "active"}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    {subscription?.plan_id === plan.id && subscription?.status === "active"
                      ? t("subscription.currentPlan")
                      : t("subscription.subscribe")}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Stripe Payment History */}
        {stripePayments.length > 0 && (
          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-500" />
                {t("subscription.cardPayments")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stripePayments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                  >
                    <div>
                      <p className="font-medium">{payment.package_name}</p>
                      <p className="text-sm text-slate-500">
                        Stripe • {payment.duration_days} jours
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatDate(payment.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatUSD(payment.amount)}</p>
                      {getStatusBadge(payment.payment_status === "paid" ? "approved" : payment.payment_status)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* {t("subscription.mobileMoney")} Payment History */}
        {mobileMoneyPayments.length > 0 && (
          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-green-500" />
                {t("subscription.mmPayments")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {mobileMoneyPayments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                  >
                    <div>
                      <p className="font-medium">{payment.plan_name}</p>
                      <p className="text-sm text-slate-500">
                        {payment.provider_name} • {t("subscription.ref", { ref: payment.reference })}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatDate(payment.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatPrice(payment.amount_expected || payment.amount || 0)} {payment.currency}</p>
                      {getStatusBadge(payment.status)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {paymentStep === "duration" && t("subscription.chooseDuration")}
              {paymentStep === "method" && t("subscription.chooseMethod")}
              {paymentStep === "stripe" && t("subscription.cardPayment")}
              {paymentStep === "mobilemoney" && t("subscription.mmPayment")}
              {paymentStep === "payment-form" && t("subscription.paymentInfo")}
            </DialogTitle>
            {selectedPlan && (
              <DialogDescription>
                {t("subscription.planLabel", { name: selectedPlan.name })}
                {selectedPriceInfo && paymentStep !== "duration" && (
                  <>{t("subscription.planPrice", { price: formatPrice(selectedPriceInfo.finalPrice), currency: selectedPlan.currency, months: selectedDuration })}</>
                )}
              </DialogDescription>
            )}
          </DialogHeader>

          {/* Step 0: Choose Duration */}
          {paymentStep === "duration" && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                {t("subscription.durationHint")}
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {durationOptions.map((option) => (
                  <div
                    key={option.months}
                    onClick={() => setSelectedDuration(option.months)}
                    className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      selectedDuration === option.months 
                        ? "border-omu-blue-500 bg-omu-blue-50" 
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {option.popular && (
                      <Badge className="absolute -top-2 -right-2 bg-green-500 text-white text-xs">
                        {t("subscription.popular")}
                      </Badge>
                    )}
                    <p className="font-bold text-lg">{option.label}</p>
                    {option.discount > 0 && (
                      <p className="text-green-600 text-sm font-medium">-{option.discount}%</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Price Summary */}
              {selectedPriceInfo && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-2 mt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t("subscription.monthlyPrice")}</span>
                    <span>{formatPrice(selectedPlan.price)} {selectedPlan.currency}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t("subscription.duration")}</span>
                    <span>{t("subscription.months", { count: selectedDuration })}</span>
                  </div>
                  {selectedPriceInfo.discountPercent > 0 && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">{t("subscription.subtotal")}</span>
                        <span className="line-through text-slate-400">
                          {formatPrice(selectedPriceInfo.totalWithoutDiscount)} {selectedPlan.currency}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>{t("subscription.discount", { percent: selectedPriceInfo.discountPercent })}</span>
                        <span>-{formatPrice(selectedPriceInfo.discountAmount)} {selectedPlan.currency}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t border-slate-200 pt-2 mt-2">
                    <div className="flex justify-between font-bold">
                      <span>{t("subscription.totalToPay")}</span>
                      <span className="text-omu-blue-600">
                        {formatPrice(selectedPriceInfo.finalPrice)} {selectedPlan.currency}
                      </span>
                    </div>
                    {selectedDuration > 1 && (
                      <p className="text-xs text-slate-500 text-right mt-1">
                        {t("subscription.perMonthEquiv", { price: formatPrice(selectedPriceInfo.pricePerMonth), currency: selectedPlan.currency })}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={closeDialog}>{t("actions.cancel")}</Button>
                <Button 
                  className="bg-omu-blue-500 hover:bg-omu-blue-600" 
                  onClick={() => setPaymentStep("method")}
                >
                  {t("actions.continue")}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 1: Choose Method */}
          {paymentStep === "method" && (
            <div className="space-y-4 py-4">
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                <div className={`flex items-center space-x-3 p-4 border-2 rounded-xl hover:bg-slate-50 cursor-pointer transition-all ${paymentMethod === "stripe" ? "border-omu-blue-500 bg-omu-blue-50" : "border-slate-200"}`}>
                  <RadioGroupItem value="stripe" id="stripe" />
                  <Label htmlFor="stripe" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5 text-blue-500" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{t("subscription.bankCard")}</p>
                          <Badge className="bg-green-100 text-green-700 text-xs">{t("subscription.recommended")}</Badge>
                        </div>
                        <p className="text-sm text-slate-500">{t("subscription.cardTypes")}</p>
                      </div>
                    </div>
                  </Label>
                </div>
                <div className={`flex items-center space-x-3 p-4 border-2 rounded-xl hover:bg-slate-50 cursor-pointer transition-all ${paymentMethod === "mobilemoney" ? "border-omu-blue-500 bg-omu-blue-50" : "border-slate-200"}`}>
                  <RadioGroupItem value="mobilemoney" id="mobilemoney" />
                  <Label htmlFor="mobilemoney" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <Smartphone className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="font-medium">{t("subscription.mobileMoney")}</p>
                        <p className="text-sm text-slate-500">{t("subscription.mmTypes")}</p>
                      </div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPaymentStep("duration")}>{t("actions.back")}</Button>
                <Button className="bg-omu-blue-500 hover:bg-omu-blue-600" onClick={handleMethodContinue}>
                  {t("actions.continue")}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Stripe Payment Step */}
          {paymentStep === "stripe" && (
            <div className="space-y-4 py-4">
              <Alert className="border-blue-200 bg-blue-50">
                <Sparkles className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  {t("subscription.stripeSecure")}
                </AlertDescription>
              </Alert>

              {/* Stripe Plans - Style unifié avec {t("subscription.mobileMoney")} */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {stripePackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    onClick={() => setSelectedStripePackage(pkg)}
                    className={`border-2 rounded-2xl p-6 cursor-pointer transition-all ${
                      selectedStripePackage?.id === pkg.id
                        ? "border-omu-blue-500 bg-omu-blue-50"
                        : "border-slate-200 hover:border-omu-blue-300"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-heading text-xl font-bold">{pkg.name}</h3>
                        <p className="text-slate-500">{pkg.duration_days} jours</p>
                      </div>
                      {pkg.name?.toLowerCase().includes("premium") && (
                        <Badge className="bg-omu-blue-500">{t("subscription.premium")}</Badge>
                      )}
                    </div>

                    <div className="mb-4">
                      <span className="text-3xl font-bold text-slate-900">
                        {formatPrice(pkg.price)}
                      </span>
                      <span className="text-slate-500 ml-1">
                        {pkg.currency || "CDF"}/{pkg.duration_days >= 365 ? "an" : "mois"}
                      </span>
                    </div>

                    {pkg.features && pkg.features.length > 0 && (
                      <ul className="space-y-2 mb-4">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {selectedStripePackage?.id === pkg.id && (
                      <div className="flex items-center justify-center gap-2 text-omu-blue-600 font-medium">
                        <Check className="h-5 w-5" />
                        {t("subscription.selected")}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPaymentStep("method")}>{t("actions.back")}</Button>
                <Button
                  className="bg-omu-blue-500 hover:bg-omu-blue-600"
                  onClick={handleStripeCheckout}
                  disabled={!selectedStripePackage || submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <ExternalLink className="h-4 w-4 mr-2" />
                  )}
                  {t("subscription.pay", { amount: selectedStripePackage ? formatUSD(selectedStripePackage.price) : "" })}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Choose {t("subscription.mobileMoney")} Provider */}
          {paymentStep === "mobilemoney" && (
            <div className="space-y-4 py-4">
              {/* Warning Notice */}
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm font-medium">
                  {notice || t("subscription.mmNotice")}
                </AlertDescription>
              </Alert>

              {/* Amount to pay */}
              <div className="bg-omu-blue-50 p-4 rounded-xl text-center">
                <p className="text-sm text-slate-600">{t("subscription.amountToPay")}</p>
                <p className="text-3xl font-bold text-omu-blue-600">
                  {selectedPriceInfo ? formatPrice(selectedPriceInfo.finalPrice) : (selectedPlan && formatPrice(selectedPlan.price))} {selectedPlan?.currency}
                </p>
                {selectedDuration > 1 && selectedPriceInfo && (
                  <p className="text-xs text-slate-500 mt-1">
                    pour {t("subscription.months", { count: selectedDuration })}
                  </p>
                )}
              </div>

              {/* Provider Selection */}
              {Object.keys(mobileMoneyAccounts).length > 0 && (
                <RadioGroup value={selectedProvider} onValueChange={setSelectedProvider}>
                  {Object.entries(mobileMoneyAccounts).map(([key, account]) => (
                    <div
                      key={key}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        selectedProvider === key ? "border-omu-blue-500 bg-omu-blue-50" : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value={key} id={key} className="mt-1" />
                        <Label htmlFor={key} className="flex-1 cursor-pointer">
                          <p className="font-semibold text-lg">{account.name}</p>
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">{t("subscription.number")}</span>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-lg">{account.id}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    copyToClipboard(account.id, key);
                                  }}
                                >
                                  {copiedId === key ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <Copy className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">{t("subscription.holder")}</span>
                              <span className="font-medium">{account.holder}</span>
                            </div>
                          </div>
                        </Label>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {Object.keys(mobileMoneyAccounts).length === 0 && (
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertTriangle className="h-4 w-4 text-blue-500" />
                  <AlertDescription className="text-blue-700">
                    Contactez-nous pour obtenir les numéros de paiement {t("subscription.mobileMoney")}, ou procédez directement avec les informations de votre paiement.
                  </AlertDescription>
                </Alert>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setPaymentStep("method")}>{t("actions.back")}</Button>
                <Button 
                  className="bg-omu-blue-500 hover:bg-omu-blue-600" 
                  onClick={() => setPaymentStep("payment-form")}
                >
                  {t("subscription.makePayment")}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 3: Payment Details Form */}
          {paymentStep === "payment-form" && (
            <div className="space-y-4 py-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  {selectedProvider
                    ? t("subscription.mmFormHint", { provider: mobileMoneyAccounts[selectedProvider]?.name || t("subscription.mobileMoney") })
                    : t("subscription.mmFormHintGeneric")}
                </AlertDescription>
              </Alert>

              {/* Opérateur {t("subscription.mobileMoney")} (si non sélectionné) */}
              {!selectedProvider && (
                <div className="space-y-2">
                  <Label htmlFor="providerName">{t("subscription.providerUsed")}</Label>
                  <Input
                    id="providerName"
                    type="text"
                    placeholder={t("subscription.providerPlaceholder")}
                    className="h-11 rounded-xl"
                    value={senderProvider}
                    onChange={(e) => setSenderProvider(e.target.value)}
                  />
                </div>
              )}

              {/* Numéro de l'expéditeur */}
              <div className="space-y-2">
                <Label htmlFor="senderPhone">{t("subscription.senderPhone")}</Label>
                <Input
                  id="senderPhone"
                  type="tel"
                  placeholder="Ex: 0812345678"
                  className="h-11 rounded-xl"
                  value={senderPhone}
                  onChange={(e) => setSenderPhone(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  {t("subscription.senderPhoneHint")}
                </p>
              </div>

              {/* {t("subscription.senderName")}/}
              <div className="space-y-2">
                <Label htmlFor="senderName">{t("subscription.senderName")}</Label>
                <Input
                  id="senderName"
                  type="text"
                  placeholder="Ex: JEAN DUPONT"
                  className="h-11 rounded-xl"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value.toUpperCase())}
                />
                <p className="text-xs text-slate-500">
                  Le nom associé au compte {t("subscription.mobileMoney")}
                </p>
              </div>

              {/* {t("subscription.transactionRef")}/}
              <div className="space-y-2">
                <Label htmlFor="transactionRef">{t("subscription.transactionRef")}</Label>
                <Input
                  id="transactionRef"
                  type="text"
                  placeholder="Ex: TXN123456789"
                  className="h-11 rounded-xl font-mono"
                  value={transactionReference}
                  onChange={(e) => setTransactionReference(e.target.value.toUpperCase())}
                />
                <p className="text-xs text-slate-500">
                  {t("subscription.transactionRefHint")}
                </p>
              </div>

              {/* Commentaire optionnel */}
              <div className="space-y-2">
                <Label htmlFor="comment">{t("subscription.commentOptional")}</Label>
                <Input
                  id="comment"
                  type="text"
                  placeholder="Informations supplémentaires..."
                  className="h-11 rounded-xl"
                  value={paymentComment}
                  onChange={(e) => setPaymentComment(e.target.value)}
                />
              </div>

              {/* Capture d'écran / Preuve de paiement */}
              <div className="space-y-2">
                <Label htmlFor="proofFile" className="flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  {t("subscription.proofScreenshot")}
                </Label>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-4 text-center transition-colors cursor-pointer ${
                    proofFile ? "border-green-300 bg-green-50" : "border-slate-300 hover:border-omu-blue-400 hover:bg-slate-50"
                  }`}
                  onClick={() => document.getElementById("proofFileInput").click()}
                  data-testid="proof-upload-zone"
                >
                  <input
                    id="proofFileInput"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error(t("subscription.toastFileTooLarge"));
                          return;
                        }
                        setProofFile(file);
                        if (proofPreview) URL.revokeObjectURL(proofPreview);
                        setProofPreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                  {proofPreview ? (
                    <div className="space-y-2">
                      <img
                        src={proofPreview}
                        alt={t("subscription.proofAlt")}
                        className="max-h-32 mx-auto rounded-lg object-contain"
                        data-testid="proof-preview"
                      />
                      <p className="text-sm text-green-700 font-medium">{proofFile?.name}</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProofFile(null);
                          URL.revokeObjectURL(proofPreview);
                          setProofPreview(null);
                        }}
                        data-testid="proof-remove-btn"
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t("actions.remove")}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 py-2">
                      <Upload className="h-8 w-8 mx-auto text-slate-400" />
                      <p className="text-sm text-slate-600">
                        {t("subscription.clickToAddScreenshot")}
                      </p>
                      <p className="text-xs text-slate-400">{t("subscription.proofFormats")}</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {t("subscription.proofHint")}
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPaymentStep("mobilemoney")}>{t("actions.back")}</Button>
                <Button
                  className="bg-green-500 hover:bg-green-600"
                  onClick={handleSubmitReference}
                  disabled={submitting || !senderPhone.trim() || !senderName.trim() || !transactionReference.trim()}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {t("actions.submit")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default PharmacySubscription;
